/**
 * api.js — Central axios client and API call definitions.
 *
 * The axios instance (`api`) is shared by all API groups below. It handles:
 *   - Base URL: defaults to '/api/v1' (proxied by Nginx in prod), overridable via REACT_APP_API_URL
 *   - Auth: attaches the JWT from localStorage as a Bearer token on every request
 *   - 401 handling: clears the token and redirects to /login on session expiry
 *                   (login requests are excluded so wrong-password errors surface normally)
 *
 * Exported API groups:
 *   authAPI     — login, register, profile
 *   adminAPI    — user approval/rejection
 *   fileAPI     — legacy file upload endpoints
 *   analysisAPI — batch analysis lifecycle (start, status, visualization, stop)
 *   projectAPI  — project CRUD and batch file/metadata uploads
 *   logAPI      — historical container logs
 *   metadataAPI — metadata sheet CRUD (list, get, upload CSV, export, versions, create, update)
 */

import axios from 'axios';

// Relative URL works in production — Nginx proxies /api/ to the backend container.
// Override with REACT_APP_API_URL in .env for local development pointing at a different host.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const authAPI = {
  /** Login uses form-encoded data (OAuth2PasswordRequestForm on the backend). */
  login: (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
    });
  },

  signup: (userData) => api.post('/auth/register', userData),

  getProfile: () => api.get('/auth/me'),
};

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------
export const adminAPI = {
  getPendingUsers: () => api.get('/auth/admin/pending-users'),

  approveUser: (userId) => api.post(`/auth/admin/approve-user/${userId}`),

  rejectUser: (userId) => api.post(`/auth/admin/reject-user/${userId}`),
};

// ---------------------------------------------------------------------------
// File (legacy — most uploads now go through projectAPI)
// ---------------------------------------------------------------------------
export const fileAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/file/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    });
  },

  uploadFromUrl: (url) => {
    return api.post('/file/upload-from-url', {
      url
    });
  },

  getUserFiles: () => api.get('/file/files'),
  getAllFiles: () => api.get('/file/files'),
  updateStatus: (fileId, status) =>
    api.put(`/file/files/${fileId}/status`, {
      status
    }),
};

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------
export const analysisAPI = {
  startAnalysis: (fileId, analysisType = 'nanopore') =>
    api.post(`/analysis/${fileId}/start?analysis_type=${analysisType}`),

  getStatus: (analysisId) => api.get(`/analysis/${analysisId}/status`),

  getResults: (analysisId) => api.get(`/analysis/${analysisId}/results`),

  getResultFileUrl: (analysisId, filename) =>
    `${API_BASE_URL}/analysis/${analysisId}/file/${filename}`,

  downloadResultFile: (analysisId, filename) =>
    api.get(`/analysis/${analysisId}/file/${filename}`, {
      responseType: 'blob'
    }),

  getAllAnalysis: () => api.get(`/analysis/get-all`),


  deleteAnalysis: (analysisId) => api.delete(`/analysis/${analysisId}`),


  getEpi2meResults: (analysisId) => api.get(`/analysis/${analysisId}/epi2me-results`),

  // Batch analysis endpoints
  getBatchStatus: (parentAnalysisId) =>
    api.get(`/analysis/${parentAnalysisId}/batch-status`),

  startVisualization: (parentAnalysisId, includeFailedAsNa = false) =>
    api.post(`/analysis/${parentAnalysisId}/start-visualization`, {
      include_failed_as_na: includeFailedAsNa
    }),

  restartSequencing: (parentAnalysisId) =>
    api.post(`/analysis/${parentAnalysisId}/restart-sequencing`),

  stopAnalysis: (analysisId) =>
    api.post(`/analysis/${analysisId}/stop`),

  updateMetadata: (analysisId, file, onProgress) => {
    const formData = new FormData();
    formData.append('metadata_file', file);
    return api.post(`/analysis/${analysisId}/update-metadata`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress,
    });
  },
};

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------
export const projectAPI = {
  create: (name, description) =>
    api.post('/project/', { name, description }),

  checkName: (name) =>
    api.get('/project/check-name', { params: { name } }),

  getAll: () => api.get('/project/'),

  getById: (projectId) => api.get(`/project/${projectId}`),

  update: (projectId, data) => api.put(`/project/${projectId}`, data),

  uploadFiles: (projectId, files, onProgress) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return api.post(`/project/${projectId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress,
    });
  },

  uploadMetadata: (projectId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/project/${projectId}/metadata`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress,
    });
  },

  startAnalysis: (projectId) => api.post(`/project/${projectId}/start`),

  delete: (projectId) => api.delete(`/project/${projectId}`),

  // Batch upload endpoints
  uploadBatchFiles: (projectId, batchIndex, files, onProgress) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return api.post(`/project/${projectId}/batch/${batchIndex}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress,
    });
  },

  uploadBatchMetadata: (projectId, batchIndex, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/project/${projectId}/batch/${batchIndex}/metadata`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress,
    });
  },

  uploadMasterMetadata: (projectId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/project/${projectId}/master-metadata`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress,
    });
  },

  startBatchSequencing: (projectId) =>
    api.post(`/project/${projectId}/start-sequencing`),

  getBatchBarcodes: (projectId, batchIndex) =>
    api.get(`/project/${projectId}/batch/${batchIndex}/barcodes`),
};

// ---------------------------------------------------------------------------
// COI / Eukaryota pipeline (QIIME 2)
// ---------------------------------------------------------------------------
export const coiAPI = {
  /** Create a new project bound to the COI pipeline. */
  createProject: (name, description) =>
    api.post('/coi/project', { name, description }),

  /** Upload one or more ZIP archives containing barcodeXX/ FASTQ folders. */
  uploadFiles: (projectId, files, onProgress) => {
    const formData = new FormData();
    files.forEach(file => { formData.append('files', file); });
    return api.post(`/coi/${projectId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },

  /** Upload the QIIME 2 manifest.csv (required). */
  uploadManifest: (projectId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/coi/${projectId}/manifest`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },

  /** Upload an optional metadata.csv used only for R-based figures. */
  uploadMetadata: (projectId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/coi/${projectId}/metadata`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },

  /** Start the pipeline for an already-uploaded COI project. */
  startAnalysis: (projectId) => api.post(`/coi/${projectId}/start`),

  /** Fetch grouped result file metadata for the COI results page. */
  getResults: (analysisId) => api.get(`/coi/${analysisId}/results`),
};

// ---------------------------------------------------------------------------
// Logs
// ---------------------------------------------------------------------------
export const logAPI = {
  getHistoricalLogs: (analysisId, phase = null) =>
    api.get(`/logs/${analysisId}`, { params: phase ? { phase } : {} })
}

// ---------------------------------------------------------------------------
// Metadata sheets
// ---------------------------------------------------------------------------
export const metadataAPI = {
  /** List all sheets across all projects for the current user, grouped by project. */
  listAll: () => api.get('/metadata/'),

  /** List all sheets for a project (summary only — no columns/rows). */
  listByProject: (projectId) => api.get(`/metadata/project/${projectId}`),

  /** Get a single sheet with full columns and rows — used to populate the editor. */
  getSheet: (sheetId) => api.get(`/metadata/${sheetId}`),

  /** Upload a CSV file to create or replace a sheet. batchIndex required when metadataType is "batch". */
  uploadCSV: (projectId, metadataType, file, batchIndex) => {
    const formData = new FormData()
    formData.append('file', file);
    // TODO: handle if batchIndex is null
    return api.post(`/metadata/project/${projectId}/upload?metadata_type=${metadataType}&batch_index=${batchIndex}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }
    )
  },

  /** Download a sheet as a CSV blob (use with URL.createObjectURL to trigger browser download). */
  exportCSV: (sheetId) => api.get(`/metadata/${sheetId}/export`,
    { responseType: 'blob', }),

  /** Get the version history for a sheet, ordered newest first. */
  getVersions: (sheetId) => {
    return api.get(`/metadata/${sheetId}/versions`)
  },

  /** Create a new sheet from the in-app editor. data: { name, type, batch_index, columns, rows } */
  createSheet: (projectId, data) => api.post(`/metadata/project/${projectId}`, data),

  /** Save a new version of an existing sheet. data: { cols, rows, change_description? } */
  updateSheet: (sheetId, data) => api.patch(`/metadata/${sheetId}`, data),

  /** Permanently delete a sheet and all its version history. */
  deleteSheet: (sheetId) => api.delete(`/metadata/${sheetId}`)
}

export default api;